import { Chart } from '@frontend/chart/types';

import { PeriodComparisonDirection } from './to-period-comparison-metric';

export type DurationMetricDirection = PeriodComparisonDirection;

export interface DurationMetric {
    /** Average duration for the most recent period bin, in seconds. */
    averageSeconds: number;
    /** Signed absolute change vs the previous period, in whole minutes (e.g. +5 / -5). */
    minuteChange: number | null;
    /** Relative percent change vs the previous period (e.g. 300 for +300%). Null when previous is 0. */
    percentChange: number | null;
    direction: DurationMetricDirection;
}

export interface DurationParts {
    days: number;
    hours: number;
    minutes: number;
}

const lastTwoSeconds = (chart: Chart): number[] => {
    const points = chart.series[0]?.data ?? [];
    return points.slice(-2).map((point) => point.value);
};

const toPercentChange = (current: number, previous: number): number | null => {
    if (previous === 0) {
        return null;
    }

    return ((current - previous) / previous) * 100;
};

export const toDurationMetric = (chart: Chart): DurationMetric | null => {
    const recentValues = lastTwoSeconds(chart);

    if (recentValues.length === 0) {
        return null;
    }

    const averageSeconds = recentValues[recentValues.length - 1];

    if (recentValues.length < 2) {
        return { averageSeconds, minuteChange: null, percentChange: null, direction: 'flat' };
    }

    const previous = recentValues[0];
    const minuteChange = Math.round((averageSeconds - previous) / 60);
    const percentChange = toPercentChange(averageSeconds, previous);

    if (minuteChange > 0) {
        return { averageSeconds, minuteChange, percentChange, direction: 'up' };
    }

    if (minuteChange < 0) {
        return { averageSeconds, minuteChange, percentChange, direction: 'down' };
    }

    return { averageSeconds, minuteChange: 0, percentChange: percentChange ?? 0, direction: 'flat' };
};

export const toDurationParts = (seconds: number): DurationParts => {
    const totalMinutes = Math.max(0, Math.round(seconds / 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    return { days, hours, minutes };
};

/** Formats a signed minute delta for display (`+5`, `-5`, `0`). */
export const formatSignedMinuteChange = (minuteChange: number): string => {
    if (minuteChange > 0) {
        return `+${minuteChange}`;
    }
    return String(minuteChange);
};

/** Formats a signed percent for display (`+300.0%`, `-12.5%`, `0.0%`). */
export const formatSignedPercentChange = (percentChange: number): string => {
    const absolute = Math.abs(percentChange).toFixed(1);

    if (percentChange > 0) {
        return `+${absolute}%`;
    }

    if (percentChange < 0) {
        return `-${absolute}%`;
    }

    return `${absolute}%`;
};

/** Formats minute delta with optional percent, e.g. `+6 (+300.0%)`. */
export const formatDurationTrendChange = (minuteChange: number, percentChange: number | null): string => {
    const value = formatSignedMinuteChange(minuteChange);

    if (percentChange === null) {
        return value;
    }

    return `${value} (${formatSignedPercentChange(percentChange)})`;
};
